import * as Sentry from '@sentry/browser'
import { Call } from '@twilio/voice-sdk'
import { Service } from '@/services/helpers/ServiceHelper'
import type { Contact, CallContact } from '@/services/types/Contact'

type CallStoreContact = Contact | CallContact

export enum CallType {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

// Priority-ordered snapshot of what the call UI should show. Higher entries win:
// NEW_INCOMING > INCOMING_RINGING > ACTIVE_WITH_HELD > ON_HOLD > ACTIVE > IDLE.
export enum CallUIStatus {
  IDLE = 'IDLE',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  ACTIVE_WITH_HELD = 'ACTIVE_WITH_HELD',
  INCOMING_RINGING = 'INCOMING_RINGING',
  NEW_INCOMING = 'NEW_INCOMING',
}

export interface ConferenceParticipant {
  callSid: string
  // The number Twilio reports for this leg. Only set for participants parsed from the event
  // stream, not for the legs we construct locally when a call starts.
  phone?: string
  contact: CallStoreContact | null,
  isOnHold?: boolean
  accepted?: boolean
  declined?: boolean
  isSpeaking?: boolean | null
}

export interface PendingParticipant {
  contact: CallStoreContact
  // The E164 number actually dialed. The participant list is matched against this, not against
  // the contact's stored phone, which can come back from the backend in any format.
  phone: string
}

export interface ConferenceEntry {
  // Primary identifier: conference SID
  id: string
  // The client's own call-leg SID (needed for Twilio SDK operations)
  clientCallSid: string | null
  // The Twilio Call object for the client's own leg of the call
  // (only available after accepting an incoming call or initiating an outgoing call)
  call: Call | null
  conferenceParticipants: ConferenceParticipant[]
  type: CallType
  muted: boolean
  duration: number
  isAddingParticipant: boolean
  isRemovingParticipant: boolean
  // The leg has not been answered yet: an incoming call still ringing, or an outgoing one still dialing.
  isRinging: boolean
  // The contact dialed into this conference, kept until it joins or drops off the participant list.
  pendingParticipant: PendingParticipant | null
}

export interface CallUpdatePayload {
  call_sid: string
  call_status: 'ringing' | 'in-progress' | 'completed'
  call_direction: 'inbound' | 'outbound-dial'
  call_waiting_state?: 'waiting'
  from_phone_number?: string
  action?: string
  conference_sid?: string
  ended_conference_sid?: string
  participants?: Array<any>
  participant_number?: string
  target_login?: string
}

// Max time to wait for an outgoing call to reach the ringing state before giving up,
// so a connect that errors or never rings cannot leave the UI stuck in "connecting".
const OUTGOING_CONNECT_TIMEOUT_MS = 45000

const waitForOutgoingCallRinging = (call: Call): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('Timed out while connecting the call'))
    }, OUTGOING_CONNECT_TIMEOUT_MS)
    function cleanup () {
      clearTimeout(timer)
      call.removeListener('ringing', onRinging)
      call.removeListener('error', onError)
      call.removeListener('disconnect', onFailure)
      call.removeListener('cancel', onFailure)
    }
    function onRinging () {
      cleanup()
      resolve(call.parameters.CallSid)
    }
    function onError (error: unknown) {
      cleanup()
      reject(error instanceof Error ? error : new Error('Outgoing call failed to connect'))
    }
    function onFailure () {
      cleanup()
      reject(new Error('Outgoing call ended before connecting'))
    }
    call.on('ringing', onRinging)
    call.on('error', onError)
    call.on('disconnect', onFailure)
    call.on('cancel', onFailure)
  })

export const useCallStore = defineStore('call', () => {
  const snackbar = useSnackbar()

  const {
    callDevice,
    requestMicPermission,
    setCallDevice,
    removeDevice,
    inputSource,
    availableInputSources,
    changeInputSource,
    outputSource,
    availableOutputSources,
    changeOutputSource,
    releaseInputSource,
    stopRingtone
  } = useCallDevice()

  // ── Core multi-call state ──────────────────────────────────────────────
  // Map keyed by conferenceSid (or temporary callSid until conferenceSid is known)
  // shallowRef: updates require reference replacement (`conferences.value = new Map(...)`).
  // Mutations via .set/.delete/.clear will NOT trigger reactivity.
  const conferences = shallowRef<Map<string, ConferenceEntry>>(new Map())
  const activeConferenceId = ref<string | null>(null)
  const heldConferenceId = ref<string | null>(null)
  const incomingRingingCallId = ref<string | null>(null)
  const isEventStreamConnected = ref(false)
  const isConnecting = ref(false)
  const connectingContact = ref<CallStoreContact | null>(null)
  const isMergingConferences = ref(false)

  // ── Getters ────────────────────────────────────────────────────────────
  const activeCall = computed(() => (activeConferenceId.value ? conferences.value.get(activeConferenceId.value) ?? null : null))
  const heldCall = computed(() => (heldConferenceId.value ? conferences.value.get(heldConferenceId.value) ?? null : null))
  const ringingCall = computed(() => (incomingRingingCallId.value ? conferences.value.get(incomingRingingCallId.value) ?? null : null))

  // ── Display derivations ────────────────────────────────────────────────
  const {
    isConference,
    status,
    ringingContact,
    activeParticipants,
    heldParticipants,
    primaryHeldContact,
    primaryActiveContact,
    isPrimaryActiveOnHold,
    isPrimaryActiveSpeaking,
    callTitle,
    activeCallLabel,
    heldCallLabel,
    callInProgress,
    dialerOpen,
    callSidebarOpen,
    lastCompletedOutgoingContact
  } = useCallDisplay({ activeCall, heldCall, ringingCall, incomingRingingCallId, isConnecting })

  // ── Participant limits ─────────────────────────────────────────────────
  const userStore = useUserStore()
  const conferenceParticipantsLimit = computed(() => userStore.currentTenantInfo?.conference_participants_limit ?? 4)
  const conferenceParticipantLimitReached = computed(() => (activeParticipants.value?.length || 0) >= conferenceParticipantsLimit.value)
  // 'CA' keys a raw Twilio call leg, not a conference, so there is nothing to add a participant to yet.
  const disableAddParticipant = computed(() =>
    !activeConferenceId.value ||
    activeConferenceId.value.startsWith('CA') ||
    !!activeCall.value?.pendingParticipant ||
    conferenceParticipantLimitReached.value
  )
  const canMergeConferences = computed(() => {
    const amountOfParticipantsOnHeldCall = heldParticipants.value?.length || 0
    const amountOfParticipantsOnActiveCall = activeParticipants.value?.length || 0
    return (amountOfParticipantsOnHeldCall + amountOfParticipantsOnActiveCall) <= conferenceParticipantsLimit.value
  })

  // ── Per-call timers ────────────────────────────────────────────────────
  const { startCallTimer, stopCallTimer, rekeyTimer } = useCallTimers(conferences)
  const callDuration = computed(() => activeCall.value?.duration ?? 0)

  // ── Call entry helpers ─────────────────────────────────────────────────

  // Find an entry by its Twilio call-leg SID (linear scan).
  const findEntryByCallSid = (callSid: string): ConferenceEntry | undefined => {
    return Array.from(conferences.value.values()).find(e => e.clientCallSid === callSid)
  }

  const createConferenceEntry = (overrides: Partial<ConferenceEntry> & Pick<ConferenceEntry, 'id' | 'type'>) => {
    const entry = {
      clientCallSid: null,
      call: null,
      conferenceParticipants: [],
      isRinging: true,
      muted: false,
      duration: 0,
      isAddingParticipant: false,
      isRemovingParticipant: false,
      pendingParticipant: null,
      ...overrides
    }
    const updated = new Map(conferences.value)
    updated.set(overrides.id, entry)
    conferences.value = updated
  }

  const patchConferenceEntry = (
    conferenceId: string,
    patch: Partial<ConferenceEntry>,
    map: Map<string, ConferenceEntry> = new Map(conferences.value)
  ) => {
    const entry = map.get(conferenceId)
    if (!entry) { return }
    map.set(conferenceId, { ...entry, ...patch })
    if (map) { conferences.value = map }
  }

  /**
   * Re-key an entry from a temporary key (usually callSid) to the real conferenceSid.
   * Updates all pointer refs (activeConferenceId, heldConferenceId, incomingRingingCallId) accordingly.
   */
  const rekeyConferenceEntry = (oldKey: string, newKey: string): void => {
    if (oldKey === newKey) { return }
    const entry = conferences.value.get(oldKey)
    if (!entry) { return }

    const updated = new Map(conferences.value)
    updated.delete(oldKey)
    updated.set(newKey, { ...entry, id: newKey })
    conferences.value = updated

    rekeyTimer(oldKey, newKey)

    // Update pointer refs
    if (activeConferenceId.value === oldKey) { activeConferenceId.value = newKey }
    if (heldConferenceId.value === oldKey) { heldConferenceId.value = newKey }
    if (incomingRingingCallId.value === oldKey) { incomingRingingCallId.value = newKey }
  }

  // The client's Twilio leg can outlive its entry: hold and swap move that same leg between conferences,
  // so ending one removes its entry while the leg keeps serving the other. Its listeners are left alone,
  // removeAllListeners() also drops the Device's own and the Device would then stay busy forever,
  // refusing to release the mic once the leg does end
  const releaseInputSourceWhenLegEnds = (call: Call | null) => {
    if (!call || call.status() === Call.State.Closed) { return }
    call.once('disconnect', releaseInputSourceIfIdle)
  }

  const removeConferenceEntry = (conferenceId: string, callSid?: string) => {
    const wasActive = activeConferenceId.value === conferenceId
    const entry = conferences.value.get(conferenceId)
    const updated = new Map(conferences.value)
    if (entry) {
      releaseInputSourceWhenLegEnds(entry.call)
      stopCallTimer(conferenceId)
      updated.delete(conferenceId)
    } else if (callSid) {
      const entryByCallSid = findEntryByCallSid(callSid)
      if (entryByCallSid) {
        releaseInputSourceWhenLegEnds(entryByCallSid.call)
        stopCallTimer(entryByCallSid.id)
        updated.delete(entryByCallSid.id)
      }
    }

    conferences.value = updated

    if (activeConferenceId.value === conferenceId) { activeConferenceId.value = null }
    if (heldConferenceId.value === conferenceId) { heldConferenceId.value = null }
    if (incomingRingingCallId.value === conferenceId) { incomingRingingCallId.value = null }

    // Auto-resume held call when the active call drops
    if (wasActive && heldConferenceId.value) {
      resumeCall(heldConferenceId.value)
    }
  }

  // ── Contact lookup ─────────────────────────────────────────────────────
  const { getCallContact, parseConferenceParticipants } = useCallContacts()
  const { phoneE164 } = useFilters()

  // ── Conference helpers ─────────────────────────────────────────────────

  const updateConferenceParticipants = async (conferenceId: string, participants: Array<any>): Promise<void> => {
    const realParticipants = await parseConferenceParticipants(participants)
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    const patch: Partial<ConferenceEntry> = {
      conferenceParticipants: realParticipants.map(p => ({
        ...p,
        isSpeaking: entry.conferenceParticipants.find(existing => existing.callSid === p.callSid)?.isSpeaking ?? null
      }))
    }
    if (entry.pendingParticipant) {
      const dialed = realParticipants.find(p => phoneE164(p.phone ?? '') === entry.pendingParticipant?.phone)
      if (!dialed || dialed.accepted || dialed.declined) { patch.pendingParticipant = null }
    }
    patchConferenceEntry(conferenceId, patch)
  }

  const addParticipant = async (conferenceId: string, phoneNumber: string, contact: CallStoreContact | null = null): Promise<boolean> => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return false }
    const pendingParticipant = contact ? { contact: { ...contact }, phone: phoneE164(phoneNumber) } : null
    patchConferenceEntry(conferenceId, { isAddingParticipant: true, pendingParticipant })
    try {
      await Service?.gql?.conferencesGql?.addParticipant(conferenceId, phoneNumber)
      return true
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to add participant')
      patchConferenceEntry(conferenceId, { pendingParticipant: null })
      return false
    } finally {
      patchConferenceEntry(conferenceId, { isAddingParticipant: false })
    }
  }

  const addParticipantToActiveConference = async (): Promise<void> => {
    const callSid = ringingCall.value?.id
    const entry = conferences.value.get(callSid!)
    if (!entry) { return }
    patchConferenceEntry(callSid!, { isAddingParticipant: true })
    try {
      await Service?.gql?.conferencesGql?.acceptIntoActiveConference(ringingCall.value!.clientCallSid!)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to add participant to active conference')
    } finally {
      patchConferenceEntry(callSid!, { isAddingParticipant: false })
      stopRingtone()
    }
  }

  const removeParticipant = async (conferenceId: string, participantSid: string): Promise<void> => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    patchConferenceEntry(conferenceId, { isRemovingParticipant: true })
    try {
      await Service?.gql?.conferencesGql?.removeParticipant(participantSid)
      const patch: Partial<ConferenceEntry> = {
        isRemovingParticipant: false
      }
      patchConferenceEntry(conferenceId, patch)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to remove participant')
      patchConferenceEntry(conferenceId, { isRemovingParticipant: false })
    }
  }

  // ── Incoming call handling ─────────────────────────────────────────────
  // incoming_call can land before the Twilio SDK's own 'incoming' event: the caller joins the
  // conference (which fires the event) while the callee's client leg is still being dialed. The
  // ringing UI and the ringtone both key off a 'CF' id, so remember the conference the caller's
  // leg belongs to and let addRingingCallEntry key the entry by it right away. Keyed by the
  // caller's call SID, which is what both sides identify the call by.
  const pendingIncomingConferences = new Map<string, string>()

  const addRingingCallEntry = async (call: Call) => {
    const callerPhoneNumber = call.customParameters.get('caller_number')
    const callSid = call.customParameters.get('caller_call_sid')
    if (!callSid) { return }
    const contact = await getCallContact(callerPhoneNumber!)
    const conferenceSid = pendingIncomingConferences.get(callSid)
    pendingIncomingConferences.delete(callSid)
    // The caller may have hung up while we resolved the contact.
    if (!contact || call.status() === Call.State.Closed) { return }
    // Looked up after the await: incoming_call may have created the entry while we resolved
    // the contact, and a second entry for the same leg would strand the one the events update.
    const existingEntry = findEntryByCallSid(callSid)
    if (existingEntry) {
      patchConferenceEntry(existingEntry.id, { call })
      incomingRingingCallId.value = existingEntry.id
      return
    }
    const id = conferenceSid ?? callSid
    createConferenceEntry({
      id,
      clientCallSid: callSid,
      call,
      conferenceParticipants: [{ callSid, contact }],
      type: CallType.INCOMING
    })
    incomingRingingCallId.value = id
  }

  const acceptRingingCall = () => {
    const entry = conferences.value.get(incomingRingingCallId.value!)
    const key = entry?.id
    if (!key) { return }
    patchConferenceEntry(key, { isRinging: false })
    activeConferenceId.value = key
    incomingRingingCallId.value = null
    startCallTimer(key)
  }

  const handleIncomingCall = async (payload: CallUpdatePayload) => {
    const callSid = payload.call_sid
    const conferenceSid = payload.conference_sid
    const needsRekey = incomingRingingCallId.value === callSid && incomingRingingCallId.value.startsWith('CA')
    if (needsRekey) {
      // The incoming call was first catched by Twilio SDK and
      // now we need to re-key it with the new conferenceSid
      rekeyConferenceEntry(incomingRingingCallId.value!, conferenceSid!)
      return
    }
    // The SDK leg may not have rung yet, or is still resolving its contact
    if (conferenceSid) { pendingIncomingConferences.set(callSid, conferenceSid) }
    const isDuplicatedCall = payload.participants?.some((p: any) => p.call_sid === activeConferenceId.value)
    if (!activeConferenceId.value || isDuplicatedCall) { return }
    const contact = await getCallContact(payload.participant_number ?? '')
    // addRingingCallEntry may have created the entry while we resolved the contact
    if (!contact || findEntryByCallSid(callSid)) { return }
    pendingIncomingConferences.delete(callSid)
    createConferenceEntry({
      id: conferenceSid!,
      clientCallSid: callSid,
      conferenceParticipants: [{ callSid, contact }],
      type: CallType.INCOMING
    })
    incomingRingingCallId.value = conferenceSid!
  }

  const handleIncomingCallEnded = (payload: CallUpdatePayload) => {
    pendingIncomingConferences.delete(payload.call_sid)
    const entry = findEntryByCallSid(payload.call_sid)
    if (!entry) { return }
    removeConferenceEntry(entry.id)
    incomingRingingCallId.value = null
    stopRingtone()
  }

  const handleConferenceMerged = (payload: CallUpdatePayload) => {
    const conferenceSid = payload.conference_sid
    const conferenceToRemove = payload.ended_conference_sid
    if (!conferences.value.has(conferenceSid!)) {
      createConferenceEntry({
        id: conferenceSid!,
        clientCallSid: payload.call_sid,
        type: CallType.INCOMING,
        isRinging: false
      })
      activeConferenceId.value = conferenceSid!
      startCallTimer(conferenceSid!)
    }
    if (conferences.value.has(conferenceToRemove!)) {
      removeConferenceEntry(conferenceToRemove!)
    }
    updateConferenceParticipants(conferenceSid!, payload.participants!)
  }

  const handleConferenceActivated = (payload: CallUpdatePayload) => {
    const conferenceSid = payload.conference_sid
    if (!conferences.value.has(conferenceSid!)) {
      rekeyConferenceEntry(activeConferenceId.value!, conferenceSid!)
    }
    patchConferenceEntry(conferenceSid!, { isRinging: false })
    updateConferenceParticipants(conferenceSid!, payload.participants!)
  }

  const handleParticipantsChanged = (payload: CallUpdatePayload) => {
    const conferenceSid = payload.conference_sid
    if (conferences.value.has(conferenceSid!)) {
      updateConferenceParticipants(conferenceSid!, payload.participants!)
    }
  }

  const handleParticipantDeclined = async (payload: CallUpdatePayload) => {
    // An outgoing call stays keyed by the client's call SID until it is re-keyed to the conference,
    // so fall back to the client's own leg, which is always in the participant list
    const entry = conferences.value.get(payload.conference_sid ?? '') ??
      payload.participants?.map(p => findEntryByCallSid(p.call_sid)).find(Boolean)
    if (!entry || entry.id === incomingRingingCallId.value) { return }
    await updateConferenceParticipants(entry.id, payload.participants ?? [])
    if (conferences.value.get(entry.id)?.conferenceParticipants.every(p => p.declined)) {
      await endCall(entry.id)
    }
  }

  const handleParticipantSpeakingChanged = (payload: CallUpdatePayload, isSpeaking: boolean) => {
    const conferenceSid = payload.conference_sid
    const entry = conferenceSid ? conferences.value.get(conferenceSid) : undefined
    if (!entry) { return }
    const updatedParticipants = entry.conferenceParticipants.map(p =>
      p.callSid === payload.call_sid ? { ...p, isSpeaking } : p
    )
    patchConferenceEntry(conferenceSid!, { conferenceParticipants: updatedParticipants })
  }

  const handleConferenceEnded = (payload: CallUpdatePayload) => {
    const conferenceSid = payload.conference_sid
    const currentHeldId = heldConferenceId.value
    if (activeConferenceId.value === conferenceSid) {
      resumeCall(currentHeldId!)
    } else if (currentHeldId === conferenceSid) {
      heldConferenceId.value = null
    }
    removeConferenceEntry(conferenceSid!)
  }

  const eventStreamHandlers: Record<string, (payload: CallUpdatePayload) => void | Promise<void>> = {
    incoming_call: handleIncomingCall,
    incoming_call_ended: handleIncomingCallEnded,
    conference_merged: handleConferenceMerged,
    conference_started: handleConferenceActivated,
    call_accepted: handleConferenceActivated,
    resume: handleConferenceActivated,
    participant_left: handleParticipantsChanged,
    participant_joined: handleParticipantsChanged,
    participant_swapped: handleParticipantsChanged,
    participant_declined: handleParticipantDeclined,
    conference_ended: handleConferenceEnded,
    participant_speaking_started: payload => handleParticipantSpeakingChanged(payload, true),
    participant_speaking_stopped: payload => handleParticipantSpeakingChanged(payload, false)
  }

  const processEventStream = async (payload: CallUpdatePayload) => {
    await eventStreamHandlers[payload.action ?? '']?.(payload)
  }

  // ── Accept / Reject Incoming SDK Call ───────────────────────────────────────────
  const acceptIncomingCall = async () => {
    const hasPermission = await requestMicPermission()
    if (!hasPermission) {
      snackbar.error('Microphone permission is required to accept calls')
      await endCall(incomingRingingCallId.value!)
      return
    }
    try {
      ringingCall.value?.call?.accept()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to accept incoming call')
    }
  }

  const clearPastConferenceEntries = async () => {
    try {
      const result = await Service?.gql?.conferencesGql?.getInProgressConferences()
      if (Array.isArray(result) && result.length === 0 && conferences.value.size > 0) {
        conferences.value = new Map()
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to fetch in-progress conferences')
    }
  }

  // ── Outgoing call ──────────────────────────────────────────────────────
  let outgoingAttemptId = 0

  const removeFailedOutgoingEntry = () => {
    if (activeCall.value?.type !== CallType.OUTGOING) { return }
    const outgoingId = activeConferenceId.value
    if (outgoingId) { removeConferenceEntry(outgoingId) }
  }

  const makeOutgoingCall = async (contact: Contact, from: string): Promise<boolean> => {
    let attempt: number | null = null
    const isCancelled = () => attempt !== null && attempt !== outgoingAttemptId
    const ownsAttempt = () => attempt !== null && attempt === outgoingAttemptId
    try {
      await clearPastConferenceEntries()
      if (conferences.value.size > 0 || isConnecting.value) {
        snackbar.error('Cannot make a new call while another call is in progress')
        return false
      }
      dialerOpen.value = false
      isConnecting.value = true
      connectingContact.value = contact
      attempt = ++outgoingAttemptId
      await clearAll()
      if (isCancelled()) { return false }

      const hasPermission = await requestMicPermission()
      if (isCancelled()) { return false }
      if (!hasPermission) {
        snackbar.error('Microphone permission is required to make calls')
        return false
      }

      const rawCall = await callDevice.value?.connect({ params: { To: contact.phone, From: from } })
      if (isCancelled()) {
        rawCall?.disconnect()
        return false
      }
      if (!rawCall) {
        snackbar.error('Failed to connect call device, please refresh and try again')
        return false
      }

      const callSid = await waitForOutgoingCallRinging(rawCall)
      if (isCancelled()) {
        rawCall.disconnect()
        return false
      }
      if (!callSid) {
        snackbar.error('Failed to initiate outgoing call')
        return false
      }
      // Temporarily key by callSid until we resolve the conferenceSid
      createConferenceEntry({
        id: callSid,
        clientCallSid: callSid,
        call: rawCall,
        conferenceParticipants: [{ callSid, contact }],
        type: CallType.OUTGOING
      })
      activeConferenceId.value = callSid
      connectingContact.value = null

      rawCall.on('error', (error) => {
        Sentry.captureException(error)
        // The leg can outlive its entry, and by then its errors are no longer this call's
        const current = findEntryByCallSid(callSid)
        if (!current) { return }
        snackbar.error('There was an error with the outgoing call')
        removeConferenceEntry(current.id)
      })

      startCallTimer(callSid)
      return true
    } catch (error) {
      Sentry.captureException(error)
      if (!isCancelled()) { snackbar.error('Failed to make outgoing call') }
      if (ownsAttempt()) { removeFailedOutgoingEntry() }
      return false
    } finally {
      if (ownsAttempt()) {
        isConnecting.value = false
        connectingContact.value = null
      }
    }
  }

  // Bumping the token invalidates whichever attempt is currently in flight.
  const cancelOutgoingCall = () => {
    outgoingAttemptId++
    callDevice.value?.disconnectAll()
    isConnecting.value = false
    connectingContact.value = null
  }

  const toggleDialer = () => {
    dialerOpen.value = !dialerOpen.value
  }

  // ── Mute / Hold / Resume ───────────────────────────────────────────────
  const toggleMuteActiveCall = () => {
    if (!activeConferenceId.value) { return }
    const entry = conferences.value.get(activeConferenceId.value)
    if (!entry) { return }
    const muted = !entry.muted
    entry.call?.mute(muted)
    const updated = new Map(conferences.value)
    updated.set(activeConferenceId.value, { ...entry, muted })
    conferences.value = updated
  }

  const holdCall = (conferenceId: string): void => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    try {
      stopCallTimer(conferenceId)
      patchConferenceEntry(conferenceId, { isRinging: false })
      if (activeConferenceId.value === conferenceId) { activeConferenceId.value = null }
      heldConferenceId.value = conferenceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to hold call')
    }
  }

  const resumeCall = (conferenceId: string) => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    try {
      patchConferenceEntry(conferenceId, { isRinging: false })
      activeConferenceId.value = conferenceId
      if (heldConferenceId.value === conferenceId) { heldConferenceId.value = null }
      startCallTimer(conferenceId)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to resume call')
    }
  }

  // ── Merge / Swap ───────────────────────────────────────────────────────
  const mergeCalls = async (): Promise<void> => {
    const currentActiveId = activeConferenceId.value
    const currentHeldId = heldConferenceId.value
    if (!currentActiveId || !currentHeldId) { return }

    const activeEntry = conferences.value.get(currentActiveId)
    const heldEntry = conferences.value.get(currentHeldId)
    if (!activeEntry || !heldEntry) { return }

    isMergingConferences.value = true
    try {
      // Send conferenceSids to the backend to merge
      await Service?.gql?.conferencesGql?.mergeCalls(
        currentActiveId,
        currentHeldId
      )
      resumeCall(currentHeldId)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to merge calls')
    } finally {
      isMergingConferences.value = false
    }
  }

  const swapCalls = async ({ endActive = false } = {}): Promise<void> => {
    const currentActiveId = activeConferenceId.value
    const currentHeldId = heldConferenceId.value
    if (!currentActiveId || !currentHeldId) { return }
    await Service?.gql?.conferencesGql?.swapCalls(currentHeldId!)
    if (endActive) {
      removeConferenceEntry(currentActiveId)
      incomingRingingCallId.value = null
    } else {
      holdCall(currentActiveId)
    }
    resumeCall(currentHeldId)
  }

  // ── Voicemail / Hold-and-Accept / End-and-Accept ───────────────────────
  const sendToVoicemail = async (conferenceSid: string): Promise<void> => {
    const entry = conferences.value.get(conferenceSid)
    if (!entry) { return }
    const callSid = entry.clientCallSid
    if (callSid) {
      try {
        await Service?.gql?.conferencesGql?.sendToVoicemail(callSid)
        incomingRingingCallId.value = null
        removeConferenceEntry(conferenceSid)
      } catch (error) {
        Sentry.captureException(error)
        snackbar.error('Failed to send call to voicemail')
      } finally {
        stopRingtone()
      }
    }
  }

  const activateIncomingCall = () => {
    const key = activeConferenceId.value!
    patchConferenceEntry(key, { isRinging: false })
    startCallTimer(key)
    incomingRingingCallId.value = null
  }

  const holdAndAccept = async (): Promise<void> => {
    if (!activeConferenceId.value || !incomingRingingCallId.value) { return }
    const ringingId = incomingRingingCallId.value
    const ringingEntry = conferences.value.get(ringingId)
    // sendToVoicemail and acceptAndHold use the callSid for the ringing call
    const ringingCallSid = ringingEntry?.clientCallSid ?? ringingId
    try {
      await Service?.gql?.conferencesGql?.acceptAndHold(ringingCallSid)
      heldConferenceId.value = activeConferenceId.value
      activeConferenceId.value = ringingId
      patchConferenceEntry(heldConferenceId.value, { isRinging: false })
      stopCallTimer(heldConferenceId.value)
      activateIncomingCall()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to hold waiting call')
    } finally {
      stopRingtone()
    }
  }

  const endAndAccept = async (): Promise<void> => {
    if (!incomingRingingCallId.value) { return }
    const ringingId = incomingRingingCallId.value
    const ringingEntry = conferences.value.get(ringingId)
    const ringingCallSid = ringingEntry?.clientCallSid ?? ringingId
    try {
      await Service?.gql?.conferencesGql?.acceptAndDrop(ringingCallSid)
      activeConferenceId.value = ringingId
      activateIncomingCall()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to accept waiting call')
    } finally {
      stopRingtone()
    }
  }

  // ── End or Leave Call / Conference ──────────────────────────────────────────────
  const leaveCall = async () => {
    const conferenceToDropId = activeConferenceId.value
    if (conferenceToDropId) {
      try {
        await Service?.gql?.conferencesGql?.leaveConference(conferenceToDropId)
        removeConferenceEntry(conferenceToDropId)
      } catch (error) {
        Sentry.captureException(error)
        snackbar.error('Failed to leave conference')
      }
    }
  }

  const endCall = async (conferenceId: string) => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    if (entry.type === CallType.INCOMING && entry.isRinging) {
      // The user is rejecting an incoming call
      entry.call?.reject()
      await sendToVoicemail(conferenceId)
      return
    }
    if (entry.type === CallType.OUTGOING && entry.isRinging) {
      // The caller is ending the conference before the callee answers
      // or the callee is sending the caller to the voicemail
      entry.call?.disconnect()
      await Service?.gql?.conferencesGql?.endConference({ callSid: conferenceId })
      removeConferenceEntry(conferenceId)
      return
    }
    await endConference(conferenceId)
  }

  const endConference = async (conferenceId: string): Promise<void> => {
    try {
      await Service?.gql?.conferencesGql?.endConference({ conferenceSid: conferenceId })
      if (heldConferenceId.value) {
        resumeCall(heldConferenceId.value)
      }
      removeConferenceEntry(conferenceId)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to end conference')
    }
  }

  // ── Microphone release ─────────────────────────────────────────────────
  // The device stays registered between calls to receive incoming ones, so nothing would otherwise
  // free the mic. isConnecting covers the outgoing window where the leg is live before its entry.
  // Twilio refuses to unset the mic while a leg is still live, so that case waits for its disconnect
  const releaseInputSourceIfIdle = () => {
    const noCallsLeft = conferences.value.size === 0 && !isConnecting.value
    if (noCallsLeft && !callDevice.value?.isBusy) { releaseInputSource() }
  }

  watch(
    () => conferences.value.size === 0 && !isConnecting.value,
    (noCallsLeft) => {
      if (noCallsLeft) { releaseInputSourceIfIdle() }
    }
  )

  // ── Full reset ─────────────────────────────────────────────────────────
  const clearAll = async (): Promise<void> => {
    callDevice.value?.disconnectAll()
    conferences.value.forEach(entry => entry.call?.removeAllListeners())
    conferences.value = new Map()
    activeConferenceId.value = null
    heldConferenceId.value = null
    incomingRingingCallId.value = null
    pendingIncomingConferences.clear()
    stopCallTimer()

    if (callDevice.value) {
      callDevice.value.removeAllListeners()
      callDevice.value.destroy()
      callDevice.value = null
      await setCallDevice()
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────
  return {
    // State
    callDevice,
    callDuration,
    activeConferenceId,
    heldConferenceId,
    incomingRingingCallId,
    isEventStreamConnected,
    isConnecting,
    connectingContact,
    isMergingConferences,
    // Getters
    activeCall,
    heldCall,
    ringingCall,
    isConference,
    status,
    ringingContact,
    activeParticipants,
    primaryActiveContact,
    isPrimaryActiveOnHold,
    isPrimaryActiveSpeaking,
    heldParticipants,
    primaryHeldContact,
    callTitle,
    activeCallLabel,
    heldCallLabel,
    callInProgress,
    callSidebarOpen,
    dialerOpen,
    lastCompletedOutgoingContact,
    disableAddParticipant,
    conferenceParticipantLimitReached,
    canMergeConferences,
    // Actions
    setCallDevice,
    removeDevice,
    processEventStream,
    acceptIncomingCall,
    toggleMuteActiveCall,
    makeOutgoingCall,
    cancelOutgoingCall,
    toggleDialer,
    swapCalls,
    sendToVoicemail,
    holdAndAccept,
    endAndAccept,
    mergeCalls,
    addParticipant,
    removeParticipant,
    addParticipantToActiveConference,
    endCall,
    leaveCall,
    // Audio
    inputSource,
    availableInputSources,
    changeInputSource,
    outputSource,
    availableOutputSources,
    changeOutputSource,
    addRingingCallEntry,
    acceptRingingCall
  }
})
